import type { FieldOption, IntakeCategoryId, IntakeCategorySelection, IntakeDomain, ProductOption } from "./types";

export type IntakeCategoryOption = {
  id: Exclude<IntakeCategoryId, "">;
  domain: Exclude<IntakeDomain, "">;
  label: string;
  description: string;
  examples: string[];
};

export type IntakeCategoryGroup = {
  id: Exclude<IntakeDomain, "">;
  label: string;
  description: string;
  options: IntakeCategoryOption[];
};

export type IntakeCategoryProfile = IntakeCategoryOption & {
  domainLabel: string;
  companyNameBaseLabel: string;
  facilityNoun: string;
  productNoun: string;
  productDetailNoun: string;
  expertNoun: string;
  strengthsLabel: string;
  serviceEnvironmentLabel: string;
  optionNamePlaceholder: string;
  includedItemsPlaceholder: string;
  recommendationPlaceholder: string;
  detailDescriptionPlaceholder: string;
  productPrecautionsPlaceholder: string;
  consultationProduct: Pick<ProductOption, "optionName" | "duration" | "includedItems" | "recommendationReason">;
  reservationUnitDefault?: string;
  visitFlowLabel: string;
  visitFlowDefault: string;
  bookingQuestionChecklistDefault: string;
  bookingQuestionOptions: FieldOption[];
  healthRestrictionsDefault: string;
  healthRestrictionOptions: FieldOption[];
  otherPrecautionsPlaceholder: string;
  sellingPointOptions: FieldOption[];
  targetCustomerOptions: FieldOption[];
  documentMode: "medical" | "standard";
};

const asOptions = (values: string[]): FieldOption[] => values.map((value) => ({ label: value, value }));

const commonBookingQuestionOptions: FieldOption[] = [
  { label: "예약자 여권 영문 이름", value: "예약자 여권 영문 이름" },
  { label: "생년월일", value: "생년월일" },
  { label: "성별", value: "성별" },
  { label: "국적", value: "국적" },
  { label: "여권번호", value: "여권번호" },
  { label: "SNS 채널", value: "SNS 채널", description: "Kakao, Line, WhatsApp, WeChat 등" },
  { label: "SNS ID", value: "SNS ID" },
  { label: "Line ID QR코드", value: "Line ID QR코드", description: "Line 앱의 내 QR코드 이미지입니다. ID 검색이 안 될 때 친구 추가용으로 사용합니다." },
  { label: "한국 내 연락 가능한 번호", value: "한국 내 연락 가능한 번호" },
  { label: "원하는 후보 날짜/시간대", value: "원하는 후보 날짜/시간대" },
  { label: "방문 인원 수", value: "방문 인원 수" },
  { label: "입국일/출국 예정일", value: "입국일/출국 예정일" },
  { label: "희망 가이드/응대 언어", value: "희망 가이드/응대 언어" },
  { label: "통역 필요 여부/희망 가이드/응대 언어", value: "통역 필요 여부/희망 가이드/응대 언어" },
];

const generalSellingPointOptions = asOptions([
  "외국어 응대 가능",
  "위치 접근성 좋음",
  "야간/주말 예약 가능",
  "관광객 방문 편의",
  "가격/패키지 경쟁력",
  "포트폴리오/후기 풍부",
  "크리에이트립 단독 혜택 있음",
]);

const generalTargetCustomerOptions = asOptions([
  "관광객",
  "유학생",
  "장기체류자",
  "가족 여행객",
  "커플",
  "친구 그룹",
  "단체 고객",
  "첫 한국 여행 고객",
]);

const medicalSellingPointOptions = asOptions([
  "외국어 응대 가능",
  "원장/전문의 직접 상담",
  "위치 접근성 좋음",
  "야간/주말 진료 가능",
  "관광객 방문 편의",
  "가격/패키지 경쟁력",
  "크리에이트립 단독 혜택 있음",
]);

const beautySellingPointOptions = asOptions([
  "외국어 응대 가능",
  "전문가 직접 상담/진행",
  "위치 접근성 좋음",
  "야간/주말 예약 가능",
  "관광객 방문 편의",
  "가격/패키지 경쟁력",
  "포트폴리오/후기 풍부",
  "크리에이트립 단독 혜택 있음",
]);

const medicalTargetCustomerOptions = asOptions([
  "관광객",
  "유학생",
  "장기체류자",
  "가격 비교 고객",
  "프리미엄 시술 관심 고객",
  "특정 고민이 있는 고객",
]);

const beautyTargetCustomerOptions = asOptions([
  "관광객",
  "유학생",
  "장기체류자",
  "첫 방문 고객",
  "촬영/행사 준비 고객",
  "스타일 변신 고객",
  "특정 고민이 있는 고객",
]);

const medicalBookingQuestionOptions: FieldOption[] = [
  ...commonBookingQuestionOptions,
  ...asOptions([
    "상담/시술 희망 내용",
    "개선 원하는 부위와 증상",
    "방문/시술 인원 수",
    "복용약·질환·임신 여부",
    "알레르기 여부",
    "초진/재진 여부",
  ]),
];

const beautyBookingQuestionOptions: FieldOption[] = [
  ...commonBookingQuestionOptions,
  ...asOptions([
    "원하는 스타일/디자인 사진",
    "희망 서비스/시술 내용",
    "기존 시술/관리 경험",
    "알레르기 또는 민감 피부 여부",
    "현장 추가금 안내 동의",
  ]),
];

const medicalHealthRestrictionOptions = asOptions([
  "임산부 사전 상담 필요",
  "특정 질환자 사전 상담 필요",
  "복용약이 있으면 사전 고지 필요",
  "시술별 제한사항 있음",
  "특별 제한 없음",
]);

const beautyHealthRestrictionOptions = asOptions([
  "임산부 사전 상담 필요",
  "알레르기 또는 민감 피부 사전 고지 필요",
  "기존 시술/관리 이력 사전 고지 필요",
  "서비스별 제한사항 있음",
  "특별 제한 없음",
]);

const tourBookingQuestionOptions: FieldOption[] = [
  ...commonBookingQuestionOptions,
  ...asOptions([
    "미팅/픽업 희망 위치",
    "숙소 위치",
    "희망 가이드 언어",
    "관심 테마(쇼핑/포토/맛집 등)",
    "아동/유아 동반 여부",
    "이동에 불편한 점",
  ]),
];

const ticketBookingQuestionOptions: FieldOption[] = [
  ...commonBookingQuestionOptions,
  ...asOptions([
    "희망 방문 날짜",
    "희망 입장 시간대",
    "성인/어린이/유아 인원 구분",
    "좌석 등급/회차 선택",
    "바우처 수령 방식",
  ]),
];

const experienceBookingQuestionOptions: FieldOption[] = [
  ...commonBookingQuestionOptions,
  ...asOptions([
    "원하는 컨셉/스타일 사진",
    "의상 사이즈",
    "신발 사이즈",
    "헤어 스타일링 포함 여부",
    "메이크업 포함 여부",
    "결과물 수령 방식",
    "알레르기 또는 민감 피부 여부",
  ]),
];

const transportBookingQuestionOptions: FieldOption[] = [
  ...commonBookingQuestionOptions,
  ...asOptions([
    "항공편 번호",
    "픽업/샌딩 일시",
    "픽업 장소",
    "목적지 주소",
    "동승 인원 수",
    "짐 개수와 크기",
    "카시트 필요 여부",
    "추가 정차/경유지",
  ]),
];

const telecomBookingQuestionOptions: FieldOption[] = [
  ...commonBookingQuestionOptions,
  ...asOptions([
    "사용 기간",
    "단말기 모델·OS",
    "eSIM 호환 여부",
    "픽업/배송 방식",
    "픽업 장소 또는 배송 주소",
    "데이터/통화 옵션 선호",
    "준비된 서류",
    "동행 목적/기관",
  ]),
];

const fnbBookingQuestionOptions: FieldOption[] = [
  ...commonBookingQuestionOptions,
  ...asOptions([
    "방문 인원 수(성인/아동 구분)",
    "음식 알레르기 / 못 먹는 재료",
    "종교적 식이 제한",
    "유아용 의자 필요 여부",
    "기념일/스페셜 요청",
    "현장 추가 결제 동의",
    "방문 예정일",
  ]),
];

const commerceBookingQuestionOptions: FieldOption[] = [
  ...commonBookingQuestionOptions,
  ...asOptions([
    "방문 예정일",
    "수령 방법(픽업/배송)",
    "픽업 예정일 또는 배송지",
    "관심 카테고리",
    "사이즈 / 컬러 선택",
    "각인/자수 문구",
    "수령 희망일",
    "알레르기 여부",
  ]),
];

const partnerBookingQuestionOptions: FieldOption[] = [
  ...commonBookingQuestionOptions,
  ...asOptions([
    "희망 서비스 내용",
    "이용 장소",
    "동행 시간",
    "사전 준비물",
    "알레르기·식이제한",
  ]),
];

const travelHealthRestrictionOptions = asOptions([
  "임산부 사전 상담 필요",
  "거동이 불편하시면 사전 고지 필요",
  "연령/신장/체중 제한 있음",
  "음주 시 참여 불가",
  "특별 제한 없음",
]);

const standardHealthRestrictionOptions = asOptions([
  "알레르기 또는 민감 피부 사전 고지 필요",
  "임산부 사전 안내 필요",
  "아동 동반 시 보호자 확인 필요",
  "특별 제한 없음",
]);

const noHealthRestrictionOptions = asOptions([
  "특별 제한 없음",
  "여권/신분증 확인 필요",
  "미성년자 이용 제한 있음",
]);

const fnbHealthRestrictionOptions = asOptions([
  "음식 알레르기 사전 고지 필요",
  "종교적 식이 제한 사전 고지 필요",
  "주류 제공 시 신분증 확인 필요",
  "임산부/아동/노약자 메뉴 제한 가능",
  "특별 제한 없음",
]);

const commerceHealthRestrictionOptions = asOptions([
  "민감성 피부 / 알레르기 사전 확인 필요",
  "음식 알레르기 사전 고지 필요",
  "주류 포함 시 만 19세 이상",
  "해외 반입 제한 사전 확인",
  "특별 제한 없음",
]);

const consultationProduct = (optionName: string, duration: string, includedItems: string, recommendationReason: string) => ({
  optionName,
  duration,
  includedItems,
  recommendationReason,
});

const medicalBase = {
  domain: "medical" as const,
  domainLabel: "메디컬",
  companyNameBaseLabel: "병원명",
  facilityNoun: "병원",
  productNoun: "시술",
  productDetailNoun: "시술별 상세내용",
  expertNoun: "의료진",
  strengthsLabel: "병원 특장점",
  serviceEnvironmentLabel: "서비스 / 이용환경",
  optionNamePlaceholder: "예: 미백 및 광조사 3회",
  includedItemsPlaceholder: "상담, 검사, 시술, 관리 등",
  recommendationPlaceholder: "외국인 고객에게 적합한 이유",
  detailDescriptionPlaceholder: "예: 시술 원리, 기대할 수 있는 변화, 진행 방식 등을 간단히 적어주세요.",
  productPrecautionsPlaceholder: "예: 시술 전후 피해야 할 음식·행동, 부작용 가능성, 재방문 필요 여부",
  consultationProduct: consultationProduct("무료예약 | 상담 후 시술 결정", "상담 후 결정", "현장 상담 후 고객에게 적합한 시술을 결정합니다.", "외국인 고객은 어떤 시술을 받을지 정하지 못한 경우가 많아, 상담 후 결정 상품을 유지하는 것을 추천합니다."),
  reservationUnitDefault: "1시간",
  visitFlowLabel: "병원 이용순서",
  visitFlowDefault: "1. Creatrip 예약확인증과 여권을 데스크에 제시합니다.\n2. 로비 대기공간에서 대기합니다.\n3. 상담실에서 시술 전 과정과 소요시간을 안내받습니다.\n4. 원장님과 맞춤 상담을 진행합니다.\n5. 안내에 따라 시술을 받습니다.\n6. 시술 후 주의사항을 안내받습니다.",
  bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n생년월일\n성별\n국적\nSNS 채널\nSNS ID\n상담/시술 희망 내용\n개선 원하는 부위와 증상\n복용약·질환·임신 여부",
  bookingQuestionOptions: medicalBookingQuestionOptions,
  healthRestrictionsDefault: "임산부 사전 상담 필요\n특정 질환자 사전 상담 필요\n복용약이 있으면 사전 고지 필요",
  healthRestrictionOptions: medicalHealthRestrictionOptions,
  otherPrecautionsPlaceholder: "예약 및 방문, 전액 결제/부가세/세금 환급 여부, 시술 후 관리, 부작용, 피해야 할 음식이나 행동 등을 최대한 상세히 적어주세요.",
  sellingPointOptions: medicalSellingPointOptions,
  targetCustomerOptions: medicalTargetCustomerOptions,
  documentMode: "medical" as const,
};

const beautyBase = {
  domain: "beauty" as const,
  domainLabel: "뷰티",
  companyNameBaseLabel: "샵명",
  facilityNoun: "샵",
  productNoun: "서비스",
  productDetailNoun: "서비스별 상세내용",
  expertNoun: "전문가",
  strengthsLabel: "샵 / 전문가 특장점",
  serviceEnvironmentLabel: "서비스 / 이용환경",
  optionNamePlaceholder: "예: K-스타일 헤어 메이크오버",
  includedItemsPlaceholder: "상담, 스타일링, 케어, 기본 제공 항목 등",
  recommendationPlaceholder: "외국인 고객에게 추천할 만한 스타일/서비스 포인트",
  detailDescriptionPlaceholder: "예: 진행 방식, 스타일 선택 기준, 결과물 또는 제공 범위를 간단히 적어주세요.",
  productPrecautionsPlaceholder: "예: 방문 전 준비사항, 현장 추가금, 지각/변경 제한, 서비스 후 관리 안내",
  consultationProduct: consultationProduct("무료예약 | 상담 후 서비스 결정", "상담 후 결정", "현장 상담 후 고객에게 적합한 서비스와 스타일을 결정합니다.", "외국인 고객은 원하는 스타일을 사진으로 상담한 뒤 결정하는 경우가 많아, 상담 후 결정 상품을 유지하는 것을 추천합니다."),
  reservationUnitDefault: "30분",
  visitFlowLabel: "이용순서",
  visitFlowDefault: "1. Creatrip 예약확인증을 직원에게 제시합니다.\n2. 예약한 서비스와 원하는 스타일을 확인합니다.\n3. 현장 추가금이 있는 경우 시작 전 안내받습니다.\n4. 직원의 안내에 따라 서비스를 이용합니다.\n5. 서비스 후 관리 방법과 주의사항을 안내받습니다.",
  bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n성별\n국적\nSNS 채널\nSNS ID\n원하는 스타일/디자인 사진\n희망 서비스/시술 내용\n방문 인원 수\n알레르기 또는 민감 피부 여부",
  bookingQuestionOptions: beautyBookingQuestionOptions,
  healthRestrictionsDefault: "알레르기 또는 민감 피부 사전 고지 필요\n기존 시술/관리 이력 사전 고지 필요\n서비스별 제한사항 있음",
  healthRestrictionOptions: beautyHealthRestrictionOptions,
  otherPrecautionsPlaceholder: "방문 전 준비사항, 스타일/디자인 변경 가능 범위, 현장 추가금, 지각 시 처리, 서비스 후 관리 방법을 최대한 상세히 적어주세요.",
  sellingPointOptions: beautySellingPointOptions,
  targetCustomerOptions: beautyTargetCustomerOptions,
  documentMode: "standard" as const,
};

const tourBase = {
  domain: "tour" as const,
  domainLabel: "투어 / 액티비티",
  companyNameBaseLabel: "업체명",
  facilityNoun: "운영사",
  productNoun: "투어",
  productDetailNoun: "투어별 상세내용",
  expertNoun: "가이드 / 강사",
  strengthsLabel: "업체 / 가이드 특장점",
  serviceEnvironmentLabel: "서비스 / 운영환경",
  optionNamePlaceholder: "예: 서울 근교 데이 투어 (영어 가이드)",
  includedItemsPlaceholder: "가이드, 차량, 입장료, 식사, 보험 등 포함 항목",
  recommendationPlaceholder: "외국인 고객에게 추천할 만한 코스/경험 포인트",
  detailDescriptionPlaceholder: "예: 코스, 이동 방식, 체류 시간, 포함/불포함 항목을 간단히 적어주세요.",
  productPrecautionsPlaceholder: "예: 미팅 시간, 우천 시 진행 여부, 준비물, 연령/체중 제한, 지각 시 처리",
  consultationProduct: consultationProduct("무료예약 | 일정 상담 후 투어 결정", "상담 후 결정", "일정과 인원, 희망 코스 확인 후 적합한 투어를 결정합니다.", "외국인 고객은 일정·언어·픽업 조건이 다르므로 상담 후 결정 옵션을 함께 두는 것을 추천합니다."),
  reservationUnitDefault: "1시간",
  visitFlowLabel: "투어 진행순서",
  visitFlowDefault: "1. 예약확정 후 안내된 미팅 포인트로 제 시간에 도착합니다.\n2. 가이드가 Creatrip 예약확인증과 예약자를 확인합니다.\n3. 안전 안내와 일정 브리핑을 들은 뒤 출발합니다.\n4. 일정에 따라 코스를 안내받습니다.\n5. 종료 지점에서 해산하거나 안내된 장소에서 하차합니다.",
  bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\nSNS 채널\nSNS ID\n한국 내 연락 가능한 번호\n방문 인원 수\n성인/아동/유아 인원 구분\n미팅/픽업 희망 위치\n희망 가이드/응대 언어",
  bookingQuestionOptions: tourBookingQuestionOptions,
  healthRestrictionsDefault: "임산부 사전 상담 필요\n거동이 불편하시면 사전 고지 필요",
  healthRestrictionOptions: travelHealthRestrictionOptions,
  otherPrecautionsPlaceholder: "판매/운영 기간, 출발 가능 요일, 최소/최대 출발 인원, 일정표, 미팅/하차 장소 및 사진, 최소 인원 미달 시 취소 안내 시점, 무료 아동/성인·아동 구분 기준, 우천 시 진행 여부를 상세히 적어주세요.",
  sellingPointOptions: asOptions(["외국어 가이드 가능", "소그룹 운영", "픽업 포함", "사진 촬영 지원", "식사 포함", "공식 라이선스 가이드", "크리에이트립 단독 혜택 있음"]),
  targetCustomerOptions: asOptions(["가족 여행객", "커플", "친구 그룹", "단체 고객", "사진/SNS 중심 고객", "첫 한국 여행 고객", "재방문 여행객"]),
  documentMode: "standard" as const,
};

const ticketBase = {
  domain: "ticket" as const,
  domainLabel: "티켓 / 입장권",
  companyNameBaseLabel: "운영사명",
  facilityNoun: "운영처",
  productNoun: "티켓",
  productDetailNoun: "티켓별 상세내용",
  expertNoun: "현장 운영팀",
  strengthsLabel: "운영사 특장점",
  serviceEnvironmentLabel: "이용환경",
  optionNamePlaceholder: "예: 평일 성인 입장권 1매",
  includedItemsPlaceholder: "입장권, 이용권, 모바일 바우처, 포함 시설 등",
  recommendationPlaceholder: "외국인 고객에게 추천할 만한 이용 포인트",
  detailDescriptionPlaceholder: "예: 사용 가능한 날짜, 시간, 구역, 교환 방식, 이용 제한을 간단히 적어주세요.",
  productPrecautionsPlaceholder: "예: 날짜 변경, 환불, 신분증 지참, 우천 시 운영, 바우처 교환 방식",
  consultationProduct: consultationProduct("무료예약 | 바우처 사용 조건 확인", "유효기간 내 사용", "바우처 사용 조건과 현장 교환 방식을 안내합니다.", "티켓은 사용 조건이 중요하므로 날짜·인원·바우처 교환 방식을 한눈에 보이게 정리하는 것을 추천합니다."),
  reservationUnitDefault: "1시간",
  visitFlowLabel: "이용순서",
  visitFlowDefault: "1. Creatrip 예약확인증(QR/바우처)을 현장 데스크에 제시합니다.\n2. 직원이 바우처를 확인하고 실물 티켓 또는 입장권을 발급합니다.\n3. 안내된 입장 게이트로 입장합니다.\n4. 시설/행사 이용 후 별도 반납 절차 없이 퇴장합니다.",
  bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\nSNS 채널\nSNS ID\n방문 인원 수\n성인/어린이/유아 인원 구분\n희망 방문 날짜\n희망 입장 시간대\n바우처 수령 방식",
  bookingQuestionOptions: ticketBookingQuestionOptions,
  healthRestrictionsDefault: "특별 제한 없음",
  healthRestrictionOptions: noHealthRestrictionOptions,
  otherPrecautionsPlaceholder: "사용 가능 기간/유효기간, 회차/입장 시간, 바우처 교환 방식, 실물 티켓 교환 장소, 무료 입장 연령 기준, 환불·날짜 변경 가능 여부를 상세히 적어주세요.",
  sellingPointOptions: asOptions(["외국어 안내 가능", "즉시 바우처 발급", "모바일 QR 입장", "정가 대비 할인", "자유 일정 사용", "우천 대비 실내", "크리에이트립 단독 혜택 있음"]),
  targetCustomerOptions: generalTargetCustomerOptions,
  documentMode: "standard" as const,
};

const experienceBase = {
  domain: "experience" as const,
  domainLabel: "체험 / 촬영",
  companyNameBaseLabel: "스튜디오명 / 업체명",
  facilityNoun: "스튜디오 / 체험장",
  productNoun: "체험",
  productDetailNoun: "체험별 상세내용",
  expertNoun: "강사 / 포토그래퍼",
  strengthsLabel: "스튜디오 / 강사 특장점",
  serviceEnvironmentLabel: "서비스 / 이용환경",
  optionNamePlaceholder: "예: K-컬처 원데이 클래스 90분",
  includedItemsPlaceholder: "강의, 재료, 의상/소품, 결과물, 사진 등 포함 항목",
  recommendationPlaceholder: "외국인 고객에게 추천할 만한 체험 포인트",
  detailDescriptionPlaceholder: "예: 진행 방식, 선택 가능한 컨셉, 결과물 또는 제공 범위를 간단히 적어주세요.",
  productPrecautionsPlaceholder: "예: 지각 시 처리, 결과물 전달 방식, 의상/소품 파손, 알레르기 제한",
  consultationProduct: consultationProduct("무료예약 | 상담 후 옵션 결정", "현장 도착 후 결정", "현장에서 컨셉·의상·옵션을 보고 최종 선택합니다.", "외국인 고객은 현장에서 컨셉과 옵션을 결정하는 경우가 많아 상담 후 결정 옵션을 유지하는 것을 추천합니다."),
  reservationUnitDefault: "30분",
  visitFlowLabel: "이용순서",
  visitFlowDefault: "1. Creatrip 예약확인증을 데스크에 제시합니다.\n2. 컨셉/소품/스타일 등 선택지를 안내받습니다.\n3. 안내에 따라 체험 또는 촬영을 진행합니다.\n4. 결과물(사진/완성품 등)을 수령하거나 안내된 방법으로 전달받습니다.",
  bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\nSNS 채널\nSNS ID\n방문 인원 수\n원하는 후보 날짜/시간대\n원하는 컨셉/스타일 사진\n결과물 수령 방식",
  bookingQuestionOptions: experienceBookingQuestionOptions,
  healthRestrictionsDefault: "알레르기 또는 민감 피부 사전 고지 필요\n특별 제한 없음",
  healthRestrictionOptions: standardHealthRestrictionOptions,
  otherPrecautionsPlaceholder: "지각/노쇼 기준, 의상/소품 교환 가능 범위, 추가 사진 구매, 원본/보정본 제공 수량, 사진 셀렉 방식, 결과물 전달 방식·시점, 동행/촬영 매너 안내를 상세히 적어주세요.",
  sellingPointOptions: asOptions(["외국어 응대 가능", "한국어 모르는 고객 환영", "사진 결과물 제공", "의상/소품 다양", "소그룹 운영", "위치 접근성 좋음", "크리에이트립 단독 혜택 있음"]),
  targetCustomerOptions: generalTargetCustomerOptions,
  documentMode: "standard" as const,
};

const transportBase = {
  domain: "transport" as const,
  domainLabel: "교통 / 이동",
  companyNameBaseLabel: "업체명",
  facilityNoun: "차량 업체",
  productNoun: "차량 서비스",
  productDetailNoun: "서비스별 상세내용",
  expertNoun: "기사 / 가이드",
  strengthsLabel: "업체 / 차량 특장점",
  serviceEnvironmentLabel: "서비스 / 차량 환경",
  optionNamePlaceholder: "예: 인천공항 ↔ 명동 호텔 픽업 (세단)",
  includedItemsPlaceholder: "차량, 기사, 대기 시간, 톨비/주차비 등",
  recommendationPlaceholder: "외국인 고객에게 추천할 만한 이동 편의 포인트",
  detailDescriptionPlaceholder: "예: 차량 종류, 탑승 가능 인원, 짐 한도, 이동 범위를 간단히 적어주세요.",
  productPrecautionsPlaceholder: "예: 무료 대기 시간, 항공편 지연 처리, 카시트, 짐 한도, 야간 추가요금",
  consultationProduct: consultationProduct("무료예약 | 상담 후 차량 결정", "상담 후 결정", "여행 일정/인원/짐 확인 후 적합한 차량과 옵션을 결정합니다.", "픽업·정차지·인원이 자주 바뀌는 외국인 고객 특성상 사전 상담 후 차량을 확정하는 옵션을 추천합니다."),
  reservationUnitDefault: "1시간",
  visitFlowLabel: "이용 흐름",
  visitFlowDefault: "1. Creatrip 예약확인증의 만남 장소로 이동합니다.\n2. 기사님이 안내판 또는 SNS로 본인 확인을 진행합니다.\n3. 짐을 차량에 싣고 목적지까지 이동합니다.\n4. 목적지 도착 후 짐을 내리고 일정이 종료됩니다.",
  bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\nSNS 채널\nSNS ID\n한국 내 연락 가능한 번호\n공항명/터미널\n항공편 번호\n픽업/샌딩 일시\n픽업 장소\n목적지 주소\n동승 인원 수\n짐 개수와 크기",
  bookingQuestionOptions: transportBookingQuestionOptions,
  healthRestrictionsDefault: "휠체어·특수 좌석 사전 고지 필요\n특별 제한 없음",
  healthRestrictionOptions: asOptions(["멀미·임산부 사전 안내 필요", "휠체어·특수 좌석 사전 고지 필요", "카시트 필요 여부 사전 고지 필요", "특별 제한 없음"]),
  otherPrecautionsPlaceholder: "공항별 미팅 장소 안내, 항공편 지연/결항 시 처리, 무료 대기 시간, 초과 대기료, 카시트 가능 연령/대수, 짐 한도, 톨비/주차비 포함 여부를 적어주세요.",
  sellingPointOptions: asOptions(["외국어 응대 가능", "공항 픽업 가능", "24시간 운영", "대형 차량 보유", "카시트 보유", "통역 가능", "크리에이트립 단독 혜택 있음"]),
  targetCustomerOptions: asOptions(["관광객", "가족 동반", "단체 여행객", "비즈니스/출장", "휠체어·유아 동반", "장거리·지방 일정 고객"]),
  documentMode: "standard" as const,
};

const telecomBase = {
  domain: "telecom" as const,
  domainLabel: "통신 / 생활",
  companyNameBaseLabel: "업체명",
  facilityNoun: "서비스 업체",
  productNoun: "서비스",
  productDetailNoun: "서비스별 상세내용",
  expertNoun: "상담원",
  strengthsLabel: "업체 / 서비스 특장점",
  serviceEnvironmentLabel: "서비스 / 이용환경",
  optionNamePlaceholder: "예: 10일 무제한 데이터 eSIM",
  includedItemsPlaceholder: "유심/eSIM 발급, 데이터 용량, 통화/문자 옵션, 픽업/배송",
  recommendationPlaceholder: "외국인 고객에게 추천할 만한 사용 편의 포인트",
  detailDescriptionPlaceholder: "예: 사용 기간, 수령 방식, 설치/반납 방법, 준비 서류를 간단히 적어주세요.",
  productPrecautionsPlaceholder: "예: eSIM 미지원 단말기, 분실·파손 보상금, 반납 장소, 본인인증 필요 여부",
  consultationProduct: consultationProduct("무료예약 | 상담 후 서비스 결정", "상담 후 결정", "방문/상담 후 고객의 일정과 사용량에 맞는 상품을 결정합니다.", "외국인 고객은 사용 기간·단말기·수령 방식이 달라 상담 후 결정 옵션을 유지하는 것을 추천합니다."),
  reservationUnitDefault: "30분",
  visitFlowLabel: "이용순서",
  visitFlowDefault: "1. Creatrip 예약확인증과 필요한 신분증을 준비합니다.\n2. 매장 방문 또는 지정 장소에서 본인 확인을 진행합니다.\n3. 서비스 신청/설치/수령을 진행합니다.\n4. 사용 방법과 반납/문의 절차를 안내받습니다.",
  bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\n이메일 주소(eSIM QR/바우처 수신용)\n한국 내 연락 가능한 번호\n입국일/출국 예정일\n사용 시작일/개통 희망일\n사용 기간\n단말기 모델·OS\neSIM 호환 여부\n픽업/배송 방식",
  bookingQuestionOptions: telecomBookingQuestionOptions,
  healthRestrictionsDefault: "특별 제한 없음",
  healthRestrictionOptions: noHealthRestrictionOptions,
  otherPrecautionsPlaceholder: "본인인증 서류, 수령/반납 장소와 예정일, 단말기/eSIM 지원 여부, 데이터 용량·속도제한, 분실·파손 비용, 이용 한도, 일정 변경 가능 시점을 상세히 적어주세요.",
  sellingPointOptions: asOptions(["외국어 응대 가능", "공항 픽업/수령 가능", "매장 접근성 좋음", "야간/주말 운영", "당일 신청 가능", "가격/패키지 경쟁력", "크리에이트립 단독 혜택 있음"]),
  targetCustomerOptions: asOptions(["단기 관광객", "장기 체류자", "유학생", "비즈니스/출장", "가족 단위", "단체"]),
  documentMode: "standard" as const,
};

const fnbBase = {
  domain: "fnb" as const,
  domainLabel: "F&B",
  companyNameBaseLabel: "매장명",
  facilityNoun: "매장",
  productNoun: "메뉴",
  productDetailNoun: "메뉴별 상세내용",
  expertNoun: "셰프 / 운영진",
  strengthsLabel: "매장 특장점",
  serviceEnvironmentLabel: "매장 분위기 / 이용환경",
  optionNamePlaceholder: "예: 한우 오마카세 디너 코스",
  includedItemsPlaceholder: "코스 구성, 음료 포함 여부, 사이드 디시 등",
  recommendationPlaceholder: "외국인 고객에게 적합한 메뉴/경험 포인트",
  detailDescriptionPlaceholder: "예: 메뉴 구성, 제공 방식, 좌석/이용 시간, 포함 음료를 간단히 적어주세요.",
  productPrecautionsPlaceholder: "예: 알레르기, 식이 제한, 좌석 시간, 지각 시 처리, 현장 추가금",
  consultationProduct: consultationProduct("무료예약 | 좌석 예약 후 메뉴 현장 선택", "좌석 예약", "예약 좌석 확보 후 현장에서 메뉴를 선택합니다.", "외국인 고객은 식이 제한과 일정이 다양해 좌석 예약 후 현장 선택 옵션을 함께 두는 것을 추천합니다."),
  reservationUnitDefault: "30분",
  visitFlowLabel: "매장 이용순서",
  visitFlowDefault: "1. Creatrip 예약확인증을 직원에게 제시합니다.\n2. 예약된 좌석으로 안내받습니다.\n3. 외국어 메뉴판 또는 다국어 안내를 받습니다.\n4. 주문 후 식사를 즐깁니다.\n5. 추가 주문이나 결제 안내를 받습니다.",
  bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\n방문 인원 수(성인/아동 구분)\n원하는 후보 날짜/시간대\n음식 알레르기 / 못 먹는 재료\n종교적 식이 제한\n현장 추가 결제 동의",
  bookingQuestionOptions: fnbBookingQuestionOptions,
  healthRestrictionsDefault: "음식 알레르기 사전 고지 필요\n종교적 식이 제한 사전 고지 필요",
  healthRestrictionOptions: fnbHealthRestrictionOptions,
  otherPrecautionsPlaceholder: "예약 시간 지각 시 좌석 유지 정책, 메뉴 변경 가능성, 환불 규정, 단체 예약 인원 제한, 알레르기/식이 제한 대응 가능 범위를 적어주세요.",
  sellingPointOptions: asOptions(["외국어 메뉴판", "다국어 응대 가능", "할랄/비건 옵션", "기념일 세팅 가능", "교통 접근성 좋음", "야간 영업 가능", "크리에이트립 단독 혜택 있음"]),
  targetCustomerOptions: asOptions(["관광객", "가족 단위", "커플/기념일 고객", "비즈니스 미팅", "단체 관광객", "유학생/장기체류자"]),
  documentMode: "standard" as const,
};

const commerceBase = {
  domain: "commerce" as const,
  domainLabel: "쇼핑 / 쿠폰",
  companyNameBaseLabel: "브랜드명 / 매장명",
  facilityNoun: "브랜드 / 매장",
  productNoun: "상품 / 쿠폰",
  productDetailNoun: "상품별 상세내용",
  expertNoun: "MD / 매장 스태프",
  strengthsLabel: "브랜드 / 매장 특장점",
  serviceEnvironmentLabel: "픽업 / 구매 환경",
  optionNamePlaceholder: "예: K-뷰티 시그니처 세트 + 사은품",
  includedItemsPlaceholder: "본품, 사은품, 샘플, 쿠폰 적용 범위, 포장 옵션 등",
  recommendationPlaceholder: "외국인 고객에게 추천할 상품/혜택 포인트",
  detailDescriptionPlaceholder: "예: 상품 구성, 픽업/배송 방식, 사용 조건, 재고 변동 가능성을 간단히 적어주세요.",
  productPrecautionsPlaceholder: "예: 쿠폰 유효기간, 적용 제외 상품, 픽업 가능 기간, 교환/환불, 해외 배송/세관",
  consultationProduct: consultationProduct("무료예약 | 매장 방문 후 쿠폰 사용", "유효기간 내 자유 방문", "매장 방문 시 사용 가능한 할인/사은품 쿠폰을 제공합니다.", "외국인 고객은 일정이 유동적이므로 유효기간 내 자유 방문 가능한 쿠폰/픽업 상품을 추천합니다."),
  reservationUnitDefault: "30분",
  visitFlowLabel: "사용 / 수령 흐름",
  visitFlowDefault: "1. Creatrip 쿠폰 또는 예약확인증을 매장 직원에게 제시합니다.\n2. 원하는 상품을 추천받거나 직접 선택합니다.\n3. 쿠폰 조건에 따라 할인/사은품을 적용받습니다.\n4. 결제 후 상품을 수령합니다.",
  bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\n방문 예정일\n관심 카테고리",
  bookingQuestionOptions: commerceBookingQuestionOptions,
  healthRestrictionsDefault: "특별 제한 없음",
  healthRestrictionOptions: commerceHealthRestrictionOptions,
  otherPrecautionsPlaceholder: "쿠폰 혜택, 사용 방법, 사용 가능 지점/매장, 유효기간, 적용 제외/중복 할인, 1인 1회 제한, 면세/세금환급 가능 여부, 픽업/배송 가능 범위, 반품/교환 정책을 적어주세요.",
  sellingPointOptions: asOptions(["외국어 응대 가능", "면세/세금환급 안내 가능", "사은품 제공", "한정판/단독 구성", "매장 접근성 좋음", "배송/픽업 가능", "크리에이트립 단독 혜택 있음"]),
  targetCustomerOptions: asOptions(["관광객", "선물 구매 고객", "팬덤 고객", "K-뷰티 관심 고객", "유학생/장기체류자", "가족 단위"]),
  documentMode: "standard" as const,
};

const partnerBase = {
  domain: "partner" as const,
  domainLabel: "기타 제휴",
  companyNameBaseLabel: "업체명",
  facilityNoun: "업체",
  productNoun: "서비스",
  productDetailNoun: "서비스별 상세내용",
  expertNoun: "담당자",
  strengthsLabel: "업체 특장점",
  serviceEnvironmentLabel: "서비스 / 이용환경",
  optionNamePlaceholder: "예: 외국인 VIP 맞춤 컨시어지",
  includedItemsPlaceholder: "상담을 통해 결정되는 서비스 구성, 포함/불포함 항목",
  recommendationPlaceholder: "외국인 고객에게 필요한 이유와 추천 포인트",
  detailDescriptionPlaceholder: "예: 서비스 범위, 진행 방식, 준비물, 결과물을 간단히 적어주세요.",
  productPrecautionsPlaceholder: "예: 견적 변경 가능성, 일정 변경, 환불 정책, 통역 가능 범위",
  consultationProduct: consultationProduct("무료예약 | 상담 후 서비스 결정", "상담 후 결정", "상담을 통해 외국인 고객에게 적합한 서비스 구성을 결정합니다.", "일정·인원·언어 조건이 자주 바뀌는 서비스는 상담 후 결정 옵션을 유지하는 것을 추천합니다."),
  reservationUnitDefault: "1시간",
  visitFlowLabel: "이용순서",
  visitFlowDefault: "1. SNS로 사전 상담을 진행합니다.\n2. 견적/일정/구성요소를 확정합니다.\n3. 서비스 시작 일자에 약속한 장소에서 진행합니다.\n4. 종료 후 추가 옵션/정산 여부를 확인합니다.",
  bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\nSNS 채널\nSNS ID\n한국 내 연락 가능한 번호\n희망 일시\n희망 서비스 내용\n방문 인원 수",
  bookingQuestionOptions: partnerBookingQuestionOptions,
  healthRestrictionsDefault: "특별 제한 없음",
  healthRestrictionOptions: standardHealthRestrictionOptions,
  otherPrecautionsPlaceholder: "견적 변경 가능성, 외국인 고객 응대 가능 언어, 환불 정책, 보안/계약서 필요 여부를 적어주세요.",
  sellingPointOptions: generalSellingPointOptions,
  targetCustomerOptions: generalTargetCustomerOptions,
  documentMode: "standard" as const,
};

const profiles: IntakeCategoryProfile[] = [
  {
    ...medicalBase,
    id: "medical_dermatology",
    label: "피부과 / 미용의원",
    description: "피부·리프팅·쁘띠·레이저·관리형 의료 상품",
    examples: ["피부과", "성형외과", "미용의원"],
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n생년월일\n성별\n국적\nSNS 채널\nSNS ID\n상담/시술 희망 내용\n개선 원하는 부위와 증상\n복용약·질환·임신 여부\n알레르기 여부",
  },
  {
    ...medicalBase,
    id: "medical_dental",
    label: "치과",
    description: "미백·라미네이트·교정·임플란트 등 치과 진료/상담 상품",
    examples: ["치과", "치아미백", "라미네이트"],
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n생년월일\n성별\n국적\nSNS 채널\nSNS ID\n상담/시술 희망 내용\n평소 치아 문제 또는 불편사항\n복용약·질환·임신 여부",
  },
  {
    ...medicalBase,
    id: "medical_oriental",
    label: "한의원",
    description: "침·뜸·부항·한방관리·체질상담 등 한방 상품",
    examples: ["한의원", "한방 체험", "통증 관리"],
    productNoun: "관리/시술",
    productDetailNoun: "관리별 상세내용",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n생년월일\n성별\n국적\nSNS 채널\nSNS ID\n방문 목적 또는 불편한 증상\n통증 부위\n증상 지속 기간 / 통증 강도\n침·뜸·부항 경험 또는 거부감\n복용약·질환·임신 여부",
  },
  {
    ...medicalBase,
    id: "medical_wellness",
    label: "수액 / 웰니스 의료",
    description: "수액·검진·웰니스 상담처럼 의료기관에서 운영하는 상품",
    examples: ["수액", "건강검진", "웰니스 클리닉"],
    productNoun: "프로그램",
    productDetailNoun: "프로그램별 상세내용",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n생년월일\n성별\n국적\nSNS 채널\nSNS ID\n희망 프로그램\n복용약·질환·임신 여부\n알레르기 여부",
  },
  {
    ...beautyBase,
    id: "beauty_hair",
    label: "헤어",
    description: "컷·염색·펌·클리닉·헤드스파 등 헤어 서비스",
    examples: ["헤어샵", "헤드스파", "클리닉"],
    optionNamePlaceholder: "예: K-스타일 컷+드라이",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n성별\n국적\nSNS 채널\nSNS ID\n원하는 스타일 사진\n모발 길이/상태\n최근 염색·펌·클리닉 이력\n방문 인원 수",
    otherPrecautionsPlaceholder: "컷/드라이·샴푸·스타일링 포함 여부, 기장 추가금, 디자이너 직급/지정 추가금, 모발 상태에 따른 시술 제한, 원하는 스타일 사진 준비, 예약시간 지각 시 처리 방식을 적어주세요.",
  },
  {
    ...beautyBase,
    id: "beauty_makeup",
    label: "메이크업",
    description: "메이크업·헤어스타일링·촬영/행사용 스타일링",
    examples: ["메이크업", "헤어스타일링", "촬영 스타일링"],
    optionNamePlaceholder: "예: K-아이돌 메이크업 + 헤어스타일링",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n성별\n국적\nSNS 채널\nSNS ID\n원하는 메이크업/헤어 사진\n민감 피부 여부\n속눈썹 연장/펌 여부\n방문 목적(촬영/행사/여행)",
    otherPrecautionsPlaceholder: "방문 전 선크림/헤어제품 사용 제한, 민감 피부 준비물, 속눈썹 연장/펌 여부, 업스타일 추가금, 예약시간 지각 시 처리 방식을 적어주세요.",
  },
  {
    ...beautyBase,
    id: "beauty_nail",
    label: "네일",
    description: "손/발 젤네일·케어·풋스파 등 네일 서비스",
    examples: ["네일", "풋케어", "젤 제거"],
    optionNamePlaceholder: "예: 손 젤네일 원컬러",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n성별\n국적\nSNS 채널\nSNS ID\n원하는 네일 디자인 사진\n젤 제거 필요 여부\n젤 연장 필요 여부\n손/발 서비스 구분\n방문 인원 수",
    otherPrecautionsPlaceholder: "젤 제거/연장 추가금, 디자인 변경 가능 범위, 파츠 추가금, 손·발 동시 예약 제한, 예약확정 후 상품 변경 제한을 적어주세요.",
  },
  {
    ...beautyBase,
    id: "beauty_semipermanent",
    label: "반영구",
    description: "눈썹·아이라인·립·헤어라인·SMP 등 반영구/아트메이크업",
    examples: ["눈썹 반영구", "립 블러싱", "헤어라인"],
    productNoun: "시술",
    productDetailNoun: "시술별 상세내용",
    expertNoun: "아티스트",
    optionNamePlaceholder: "예: Eyebrow semi-permanent makeup",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n생년월일\n성별\n국적\nSNS 채널\nSNS ID\n희망 부위\n이전 반영구/아트메이크업 경험\n최근 1개월 이내 눈 주변 피부과 시술/성형수술 여부\n상담 원하는 내용\n헤르페스/알레르기/임신 여부\n방문 인원 수",
    healthRestrictionsDefault: "임산부 사전 상담 필요\n알레르기 또는 민감 피부 사전 고지 필요\n헤르페스/당뇨/고혈압 등 사전 상담 필요",
    otherPrecautionsPlaceholder: "아이라인 시술 전 속눈썹 연장 제거, 헤르페스/임신/당뇨/고혈압 제한, 리터치 기간, 시술 후 관리, 세금 환급 여부를 적어주세요.",
  },
  {
    ...beautyBase,
    id: "beauty_personal_color",
    label: "퍼스널컬러",
    description: "퍼스널컬러 진단·이미지 컨설팅·스타일링 상담",
    examples: ["퍼스널컬러", "이미지 컨설팅", "스타일 상담"],
    productNoun: "프로그램",
    productDetailNoun: "프로그램별 상세내용",
    expertNoun: "컨설턴트",
    optionNamePlaceholder: "예: 퍼스널컬러 진단 1인",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n성별\n국적\nSNS 채널\nSNS ID\n방문 인원 수\n원하는 상담 내용\n평소 선호 스타일/색상",
    healthRestrictionsDefault: "특별 제한 없음",
    otherPrecautionsPlaceholder: "민낯/렌즈/메이크업 상태, 동반 인원, 진단 결과 제공 방식, 예약시간 지각 시 처리 방식을 적어주세요.",
  },
  {
    ...beautyBase,
    id: "beauty_esthetic",
    label: "에스테틱 / 피부관리",
    description: "피부관리·윤곽관리·바디관리·스킨케어 프로그램",
    examples: ["에스테틱", "피부관리", "바디관리"],
    productNoun: "관리",
    productDetailNoun: "관리별 상세내용",
    optionNamePlaceholder: "예: 윤곽관리 60분",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n성별\n국적\nSNS 채널\nSNS ID\n개선 원하는 부위와 증상\n피부 타입/민감도\n알레르기 여부\n방문 인원 수",
    otherPrecautionsPlaceholder: "관리 전후 피해야 할 행동, 민감 피부 안내, 관리 소요시간, 현장 추가금, 임산부/특정 질환 제한 여부를 적어주세요.",
  },
  {
    ...beautyBase,
    id: "beauty_other",
    label: "기타 뷰티",
    description: "마사지·스파·속눈썹·왁싱 등 뷰티/관리형 서비스",
    examples: ["스파", "속눈썹", "왁싱"],
    productNoun: "서비스",
    productDetailNoun: "서비스별 상세내용",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n성별\n국적\nSNS 채널\nSNS ID\n희망 서비스 내용\n알레르기 또는 민감 피부 여부\n방문 인원 수",
  },
  {
    ...tourBase,
    id: "tour_day_tour",
    label: "일일투어 / 근교투어",
    description: "가이드·차량·코스가 포함된 하루 또는 반나절 투어",
    examples: ["DMZ", "남이섬", "부산 데이투어"],
  },
  {
    ...tourBase,
    id: "tour_activity",
    label: "액티비티 / 야외체험",
    description: "스포츠·야외·계절 액티비티와 강습형 체험",
    examples: ["서핑", "스키", "패러글라이딩"],
    productNoun: "액티비티",
    productDetailNoun: "액티비티별 상세내용",
    expertNoun: "강사 / 인스트럭터",
    optionNamePlaceholder: "예: 초보자 서핑 강습 2시간",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\nSNS 채널\nSNS ID\n방문 인원 수\n참가자 나이/체중\n희망 시간대",
    healthRestrictionsDefault: "임산부 사전 상담 필요\n심혈관 질환 등 사전 고지 필요\n음주 시 참여 불가\n연령/신장/체중 제한 있음",
    otherPrecautionsPlaceholder: "참여 가능 연령/체중/신장, 음주·임신·질환 제한, 우천·강풍 시 운영 정책, 장비 분실 시 처리를 적어주세요.",
  },
  {
    ...ticketBase,
    id: "ticket_attraction_festival",
    label: "입장권 / 축제 / 공연",
    description: "관광 시설, 축제, 공연, 패스처럼 바우처로 이용하는 상품",
    examples: ["전망대", "축제", "공연/쇼"],
  },
  {
    ...experienceBase,
    id: "experience_class",
    label: "체험 / 클래스",
    description: "쿠킹·공예·K-pop·문화 체험처럼 직접 참여하는 상품",
    examples: ["쿠킹 클래스", "공예", "K-pop 댄스"],
    productNoun: "클래스",
    productDetailNoun: "클래스별 상세내용",
    expertNoun: "강사",
    healthRestrictionsDefault: "알레르기 또는 식이제한 사전 고지 필요\n특별 제한 없음",
  },
  {
    ...experienceBase,
    id: "experience_photo_studio",
    label: "사진 / 스튜디오",
    description: "프로필·증명사진·K-pop 컨셉·우정/커플/가족 촬영",
    examples: ["프로필 사진", "K-pop 컨셉", "우정 사진"],
    productNoun: "촬영",
    productDetailNoun: "촬영별 상세내용",
    expertNoun: "포토그래퍼",
    optionNamePlaceholder: "예: K-아이돌 컨셉 프로필 (1인, 헤어메이크업 포함)",
    includedItemsPlaceholder: "촬영 시간, 의상 코디, 헤어·메이크업, 원본/보정본 수량, 결과물 전달 방식",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n성별\n국적\nSNS 채널\nSNS ID\n방문 인원 수\n원하는 컨셉/스타일 사진\n헤어 스타일링 포함 여부\n메이크업 포함 여부\n결과물 수령 방식",
    otherPrecautionsPlaceholder: "보정본 추가 비용, 원본 추가 비용, 외부 의상/소품 반입 가능 여부, 촬영 소요 시간, 결과물 전달 시점을 적어주세요.",
  },
  {
    ...experienceBase,
    id: "experience_hanbok_costume",
    label: "한복 / 의상대여",
    description: "한복, 교복, 전통의상 등 관광지 의상 대여 상품",
    examples: ["한복", "교복 대여", "전통의상"],
    productNoun: "대여 상품",
    productDetailNoun: "대여 옵션별 상세내용",
    expertNoun: "스태프",
    optionNamePlaceholder: "예: 프리미엄 한복 2시간 (헤어 포함)",
    includedItemsPlaceholder: "의상 1벌, 속치마, 신발, 가방, 헤어 스타일링, 보관함",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n성별\n국적\nSNS 채널\nSNS ID\n한국 내 연락 가능한 번호\n이용 일시\n대여 시간\n방문 인원 수\n의상 사이즈\n신발 사이즈\n헤어 스타일링 포함 여부",
    otherPrecautionsPlaceholder: "대여 가능 사이즈 범위, 헤어/메이크업 가능 여부, 시간 초과 연체료, 오염/파손 보상금, 보관함/소지품 보관 여부를 적어주세요.",
  },
  {
    ...transportBase,
    id: "transport_airport_pickup",
    label: "공항픽업 / 샌딩",
    description: "공항과 호텔·숙소를 잇는 픽업/샌딩 차량 상품",
    examples: ["인천공항 픽업", "공항 샌딩", "호텔↔공항"],
  },
  {
    ...transportBase,
    id: "transport_private_car",
    label: "차량 대절 / 이동 서비스",
    description: "기사 동행 차량, 전세버스, 시티투어 차량, 단체 셔틀",
    examples: ["전세버스", "시티투어 차량", "단체 셔틀"],
    optionNamePlaceholder: "예: 서울 1일 시티투어 차량 (11인승)",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\nSNS 채널\nSNS ID\n한국 내 연락 가능한 번호\n이용 일자\n시작·종료 시각\n시작 장소\n희망 코스/경유지\n동승 인원 수\n짐 개수와 크기",
  },
  {
    ...telecomBase,
    id: "telecom_esim_wifi",
    label: "통신 / eSIM / 와이파이",
    description: "유심·eSIM·포켓와이파이처럼 여행 중 통신을 돕는 상품",
    examples: ["eSIM", "유심", "포켓와이파이"],
  },
  {
    ...telecomBase,
    id: "telecom_living_support",
    label: "생활 / 숙소 / 체류 지원",
    description: "짐 보관·배송, 통역 동행, 한국살기 숙소/체류 행정 지원 서비스",
    examples: ["짐 배송", "통역 동행", "한국살기 숙소"],
    productNoun: "생활 서비스",
    optionNamePlaceholder: "예: 공항 → 호텔 짐 배송 (캐리어 1개)",
    includedItemsPlaceholder: "픽업, 배송, 보관, 통역 동행, 서류 검토 등",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\nSNS 채널\nSNS ID\n한국 내 연락 가능한 번호\n희망 일시\n픽업/방문 장소\n목적지 주소\n준비된 서류",
    otherPrecautionsPlaceholder: "짐 보관/배송은 분실·파손 보상 한도와 금지 물품을, 숙소/체류 상품은 객실 유형·기준/최대 인원·체크인/아웃·보증금·공용시설·CCTV/락커·미성년자/혼숙 가능 여부를 적어주세요.",
  },
  {
    ...fnbBase,
    id: "fnb_restaurant",
    label: "F&B 예약",
    description: "식당·카페·바처럼 좌석/시간대를 예약하고 방문하는 상품",
    examples: ["식당", "오마카세", "카페/디저트"],
  },
  {
    ...fnbBase,
    id: "fnb_coupon",
    label: "F&B 쿠폰 / 식사권",
    description: "매장 방문 후 쿠폰, 식사권, 세트권을 사용하는 상품",
    examples: ["식사권", "카페 쿠폰", "디저트 교환권"],
    productNoun: "쿠폰 / 식사권",
    productDetailNoun: "쿠폰별 사용 조건",
    optionNamePlaceholder: "예: 시그니처 디저트 세트 교환권",
    includedItemsPlaceholder: "사용 가능 메뉴, 할인율, 교환 가능 상품, 유효기간",
    consultationProduct: consultationProduct("무료예약 | 매장 방문 후 쿠폰 사용", "유효기간 내 자유 방문", "매장 방문 시 사용할 수 있는 쿠폰/식사권을 제공합니다.", "외국인 고객은 일정이 유동적이므로 유효기간 내 자유 방문 가능한 쿠폰 상품을 추천합니다."),
    visitFlowLabel: "쿠폰 사용 흐름",
    visitFlowDefault: "1. Creatrip 쿠폰을 매장 직원에게 제시합니다.\n2. 사용 가능한 메뉴 또는 상품을 확인합니다.\n3. 쿠폰 조건에 따라 교환/할인을 적용받습니다.\n4. 추가 주문이 있으면 현장에서 결제합니다.",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\n음식 알레르기 / 못 먹는 재료",
  },
  {
    ...commerceBase,
    id: "commerce_retail_coupon",
    label: "매장 쿠폰 / 리테일",
    description: "화장품·건강식품·라이프스타일 매장 방문 쿠폰",
    examples: ["화장품 쿠폰", "건강식품", "기념품 매장"],
  },
  {
    ...commerceBase,
    id: "commerce_shopping",
    label: "쇼핑 / 상품 구매",
    description: "사전 구매 후 픽업/배송하는 실물 상품",
    examples: ["K-뷰티 상품", "패션", "K-pop 굿즈"],
    productNoun: "상품",
    productDetailNoun: "상품별 상세내용",
    consultationProduct: consultationProduct("무료예약 | 매장 픽업", "픽업 가능 영업시간 내", "주문한 상품의 매장 픽업 또는 배송 방법을 안내합니다.", "여행 일정에 맞춰 매장 픽업이 가능하도록 기본 픽업 상품을 두는 것을 추천합니다."),
    visitFlowDefault: "1. 크리에이트립에서 상품을 주문하고 결제합니다.\n2. 픽업 또는 배송 방법을 선택합니다.\n3. 픽업: 예약확인증을 매장에 제시하고 상품을 수령합니다.\n4. 배송: 입력한 주소로 상품을 받습니다.",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\n수령 방법(픽업/배송)\n픽업 예정일 또는 배송지\n사이즈 / 컬러 선택\n주문 후 준비기간 확인",
  },
  {
    ...commerceBase,
    id: "commerce_custom",
    label: "맞춤 제작 / 주문제작",
    description: "각인·자수·공방·맞춤 의류처럼 제작 리드타임이 있는 상품",
    examples: ["각인", "자수", "맞춤 의류"],
    productNoun: "맞춤 제작 상품",
    productDetailNoun: "제작 옵션 상세",
    expertNoun: "장인 / 디자이너",
    companyNameBaseLabel: "공방명 / 브랜드명",
    optionNamePlaceholder: "예: 가죽 카드지갑 이니셜 각인",
    includedItemsPlaceholder: "기본 재질, 각인/자수 옵션, 포장 등",
    consultationProduct: consultationProduct("무료예약 | 상담 후 옵션 결정", "상담 후 제작 일정 안내", "디자인/사이즈/각인 등 옵션 상담을 진행합니다.", "맞춤 제작 상품은 옵션이 다양해 상담 후 결정을 추천합니다."),
    visitFlowLabel: "주문 / 제작 / 수령 흐름",
    visitFlowDefault: "1. 크리에이트립에서 옵션 상담 또는 주문을 진행합니다.\n2. 방문 또는 온라인 상담으로 디자인/사양을 결정합니다.\n3. 결제 후 제작에 들어갑니다.\n4. 완성된 상품을 매장 픽업 또는 배송으로 수령합니다.",
    bookingQuestionChecklistDefault: "예약자 여권 영문 이름\n국적\nSNS 채널\nSNS ID\n레터링/각인/자수 문구\n원하는 컬러/재질/맛\n수령 희망일\n수령 방법(픽업/배송)",
    otherPrecautionsPlaceholder: "최소 제작 소요일, 레터링/각인/자수 문구, 글자 수 제한, 디자인/색상/맛 선택, 사진 업로드 필요 여부, 변경 가능 마감, 픽업 가능 시간, 해외 배송 가능 여부를 적어주세요.",
  },
  {
    ...partnerBase,
    id: "partner_misc",
    label: "기타 프로젝트 / 제휴",
    description: "위 유형에 딱 맞지 않는 외국인 대상 맞춤·프로젝트성 서비스",
    examples: ["스마트패스", "VIP 컨시어지", "기타 제휴"],
  },
];

function splitDefaultLines(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function uniqueOptions(options: FieldOption[]): FieldOption[] {
  const seen = new Set<string>();
  return options.filter((option) => {
    if (seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
}

function includeDefaultLinesAsOptions(options: FieldOption[], defaultValue: string): FieldOption[] {
  const optionValues = new Set(options.map((option) => option.value));
  const defaultOptions = splitDefaultLines(defaultValue)
    .filter((value) => !optionValues.has(value))
    .map((value) => ({ label: value, value }));
  return uniqueOptions([...options, ...defaultOptions]);
}

const normalizedProfiles: IntakeCategoryProfile[] = profiles.map((profile) => ({
  ...profile,
  bookingQuestionOptions: includeDefaultLinesAsOptions(profile.bookingQuestionOptions, profile.bookingQuestionChecklistDefault),
  healthRestrictionOptions: includeDefaultLinesAsOptions(profile.healthRestrictionOptions, profile.healthRestrictionsDefault),
}));

export const CATEGORY_PROFILES = normalizedProfiles;

export const CATEGORY_GROUPS: IntakeCategoryGroup[] = [
  {
    id: "medical",
    label: "메디컬",
    description: "피부과, 치과, 한의원처럼 의료 상담과 시술/진료가 포함되는 상품",
    options: normalizedProfiles.filter((profile) => profile.domain === "medical"),
  },
  {
    id: "beauty",
    label: "뷰티",
    description: "헤어, 메이크업, 네일, 반영구, 에스테틱처럼 스타일·관리 중심의 상품",
    options: normalizedProfiles.filter((profile) => profile.domain === "beauty"),
  },
  {
    id: "tour",
    label: "투어 / 액티비티",
    description: "일일투어, 가이드 투어, 야외 액티비티처럼 일정과 진행 방식이 중요한 상품",
    options: normalizedProfiles.filter((profile) => profile.domain === "tour"),
  },
  {
    id: "ticket",
    label: "티켓 / 입장권",
    description: "입장권, 축제, 공연, 패스처럼 바우처와 사용 조건이 중요한 상품",
    options: normalizedProfiles.filter((profile) => profile.domain === "ticket"),
  },
  {
    id: "experience",
    label: "체험 / 촬영 / 대여",
    description: "사진, 한복, 클래스처럼 현장에서 경험하거나 결과물을 받는 상품",
    options: normalizedProfiles.filter((profile) => profile.domain === "experience"),
  },
  {
    id: "transport",
    label: "교통 / 이동",
    description: "공항픽업, 차량 대절, 셔틀처럼 이동 조건과 픽업 정보가 중요한 상품",
    options: normalizedProfiles.filter((profile) => profile.domain === "transport"),
  },
  {
    id: "telecom",
    label: "통신 / 생활",
    description: "eSIM, 와이파이, 짐 보관, 통역 동행처럼 여행·체류를 돕는 서비스",
    options: normalizedProfiles.filter((profile) => profile.domain === "telecom"),
  },
  {
    id: "fnb",
    label: "F&B",
    description: "식당 예약, 카페, 디저트, 식사권처럼 먹고 마시는 경험과 쿠폰",
    options: normalizedProfiles.filter((profile) => profile.domain === "fnb"),
  },
  {
    id: "commerce",
    label: "쇼핑 / 쿠폰 / 제작",
    description: "매장 쿠폰, 실물 상품, 맞춤 제작처럼 구매·수령·사용 조건이 중요한 상품",
    options: normalizedProfiles.filter((profile) => profile.domain === "commerce"),
  },
  {
    id: "partner",
    label: "기타 제휴",
    description: "정해진 유형에 들어가지 않는 외국인 대상 프로젝트성 서비스",
    options: normalizedProfiles.filter((profile) => profile.domain === "partner"),
  },
];

const profileById = new Map<IntakeCategoryProfile["id"], IntakeCategoryProfile>(normalizedProfiles.map((profile) => [profile.id, profile]));
const domainIds = new Set(CATEGORY_GROUPS.map((group) => group.id));

function createFallbackCategoryProfile(categoryId: string): IntakeCategoryProfile | null {
  const [domainCandidate = ""] = categoryId.split("_");
  if (!domainIds.has(domainCandidate as Exclude<IntakeDomain, "">)) return null;
  const domain = domainCandidate as Exclude<IntakeDomain, "">;
  const template = normalizedProfiles.find((profile) => profile.domain === domain) ?? getDefaultCategoryProfile();
  const label = categoryId
    .replace(`${domain}_`, "")
    .split(/[_-]+/)
    .filter((part) => part && part !== "custom")
    .join(" ") || "새 카테고리";
  return {
    ...template,
    id: categoryId as Exclude<IntakeCategoryId, "">,
    domain,
    label,
    description: `${label} 유형의 파트너 상품`,
    examples: [],
  };
}

function getCategorySlug(profile: IntakeCategoryProfile): string {
  return profile.id.replace(`${profile.domain}_`, "").replace(/_/g, "-");
}

export function getCategoryPath(categoryId: IntakeCategoryId): string {
  const profile = getCategoryProfile(categoryId);
  if (!profile) return "/";
  return `/${profile.domain}/${getCategorySlug(profile)}`;
}

export function getCategoryIdFromPathname(pathname: string): IntakeCategoryId {
  const [domain = "", slug = ""] = pathname.split("/").filter(Boolean);
  if (!domain || !slug) return "";
  const profile = normalizedProfiles.find((candidate) => candidate.domain === domain && getCategorySlug(candidate) === slug);
  return profile?.id ?? "";
}

export function getCategoryProfile(categoryId?: string): IntakeCategoryProfile | null {
  if (!categoryId) return null;
  return profileById.get(categoryId as IntakeCategoryProfile["id"]) ?? createFallbackCategoryProfile(categoryId);
}

export function getDefaultCategoryProfile(): IntakeCategoryProfile {
  return profileById.get("medical_dental")!;
}

export function createCategorySelection(categoryId: IntakeCategoryId): IntakeCategorySelection {
  const profile = getCategoryProfile(categoryId);
  return {
    domain: profile?.domain ?? "",
    categoryId: profile?.id ?? "",
    label: profile?.label ?? "",
  };
}
